import HttpService from './HttpService';

const API_URL = '/account';

const AccountService = {
  /**
   * Request account deletion. Starts the grace period; the account will be
   * permanently deleted after ACCOUNT_DELETION_GRACE_PERIOD_DAYS days
   * unless cancelled. Idempotent — re-requesting does not reset the clock.
   * Does not cancel a Stripe subscription (the account is still recoverable);
   * `subscription_will_be_canceled` says whether the purge that completes the
   * deletion will.
   *
   * @param {string} [reason] Optional free-text reason for analytics.
   * @returns {Promise<{pending: true, deletion_requested_at: string, scheduled_deletion_at: string, grace_period_days: number, subscription_will_be_canceled: boolean}>}
   */
  requestDeletion: async (reason) => {
    try {
      const response = await HttpService.post(`${API_URL}/request-deletion`, { reason: reason || null });
      return response.data;
    } catch (error) {
      console.error('Error requesting account deletion:', error);
      throw error;
    }
  },

  /**
   * Cancel a pending account deletion. No-op if not pending.
   * @returns {Promise<{cancelled: boolean}>}
   */
  cancelDeletion: async () => {
    try {
      const response = await HttpService.post(`${API_URL}/cancel-deletion`);
      return response.data;
    } catch (error) {
      console.error('Error cancelling account deletion:', error);
      throw error;
    }
  },

  /**
   * Get current pending-deletion status for the authenticated user.
   * Called on auth bootstrap so the recovery banner can render with the
   * precise scheduled-deletion timestamp.
   * @returns {Promise<{pending: boolean, deletion_requested_at: string|null, scheduled_deletion_at: string|null, grace_period_days: number, subscription_will_be_canceled: boolean}>}
   */
  getDeletionStatus: async () => {
    try {
      const response = await HttpService.get(`${API_URL}/deletion-status`);
      return response.data;
    } catch (error) {
      console.error('Error fetching account deletion status:', error);
      throw error;
    }
  },
};

export default AccountService;
